import React, { useReducer } from 'react';

import { Header } from '../components/Header';
import { PageMetadata } from '../components/PageMetadata';
import { Footer } from '../components/Footer/Footer';
import { BasicsStep } from '../components/careers/BasicsStep';
import { ConfirmationStep } from '../components/careers/ConfirmationStep';
import { LoadingStep } from '../components/LoadingStep';
import { ErrorStep } from '../components/ErrorStep';
import * as analytics from '../lib/analytics';

type CareerStep = 'BASICS' | 'SUBMIT' | 'CONFIRMATION';

interface CareersState {
  didError: boolean;
  step: CareerStep;
  values: {};
}

interface CareersAction {
  type: 'CONTINUE' | 'ERROR';
  values?: {};
}

export default function Careers() {
  const initialState = {
    didError: false,
    step: 'BASICS' as 'BASICS',
    values: {},
  };

  const [formState, dispatch] = useReducer<
    (state: CareersState, action: CareersAction) => CareersState
  >((state, action) => {
    switch (action.type) {
      case 'CONTINUE':
        let nextStep: CareerStep;

        switch (state.step) {
          case 'BASICS':
            nextStep = 'SUBMIT';

            break;
          case 'SUBMIT':
            nextStep = 'CONFIRMATION';

            break;
          default:
            nextStep = state.step;

            break;
        }

        return {
          ...state,
          step: nextStep,
          values: { ...state.values, ...action.values },
        };
      case 'ERROR':
        // Indicate something bad happened (e,g, we got a failed response from the server)
        return {
          ...state,
          didError: true,
        };
      default:
        return state;
    }
  }, initialState);

  React.useEffect(() => {
    switch (formState.step) {
      // When transitioning to the "SUBMIT" step, make the corresponding API request
      case 'SUBMIT':
        fetch('/api/careers-inquire', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formState.values),
        })
          .then((res) => res.json())
          .then((json) => {
            if (json.ok) {
              dispatch({ type: 'CONTINUE', values: {} });
            } else {
              dispatch({ type: 'ERROR' });
            }
          })
          .catch((error) => {
            console.error(error);
          });
        break;
    }
  }, [formState.step, formState.values]);

  React.useEffect(() => {
    // Log an analytics event for every step in the form

    analytics.logEvent({
      category: 'Careers',
      name: `Started step`,
      step: formState.step.toLowerCase(),
    });
  }, [formState.step]);

  React.useEffect(() => {
    // Scroll to top whenever the step changes
    window.scrollTo(0, 0);
  }, [formState.step]);

  const activeStep = (() => {
    switch (formState.step) {
      case 'BASICS':
        return (
          <BasicsStep
            onSubmit={(values: {}) => {
              dispatch({
                type: 'CONTINUE',
                values,
              });
            }}
          />
        );
      case 'SUBMIT':
        return formState.didError ? (
          <ErrorStep description="Error submitting career form" />
        ) : (
          <LoadingStep />
        );
      case 'CONFIRMATION':
        return <ConfirmationStep />;
    }
  })();

  return (
    <>
      <PageMetadata
        pageTitle="Careers"
        description="Health Plans of NC is on the lookout for talented, North Carolina licensed agents with experience in sales, marketing & lead generation. Contact us today."
      />
      <Header />
      <div className="w-full px-4 py-8 lg:py-16 bg-gray-200">
        <div className="max-w-3xl mx-auto shadow rounded bg-white">
          {activeStep}
        </div>
      </div>
      <Footer />
    </>
  );
}
